import { useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useCameraSearch } from "@visioni/a3s-dir/app";
import { useEnvironment } from "@src/config/environment-store";
import { directory } from "@src/config/runtime";
import { authenticatedFetch } from "@src/features/auth";
import { useAuthReady } from "@src/features/auth/auth-store";
import { useTenant } from "@src/features/tenants";
import { View, FormInput } from "@src/components";
import { Icon } from "@src/components/Icon";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import type { SortState } from "@src/features/clips/types/camera";
import { SortDropdown } from "./SortDropdown.web";
import { CameraSearchResults } from "./CameraSearchResults.web";

interface SearchAndSortBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: SortState;
  onSortChange: (sortBy: SortState) => void;
  isLoading?: boolean;
}

export function SearchAndSortBar({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  isLoading,
}: SearchAndSortBarProps) {
  const { theme } = useUnistyles();
  const env = useEnvironment();
  const authReady = useAuthReady();
  const isCameraSearchEnabled = useBooleanFlag(FEATURE_FLAGS.CAMERA_SEARCH);
  const { selectedTenant } = useTenant();

  const [debouncedValue, setDebouncedValue] = useState("");
  const [showResults, setShowResults] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!showResults) return;
    const handleMouseDown = (e: MouseEvent) => {
      const node = containerRef.current as unknown as HTMLElement | null;
      if (node && !node.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [showResults]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!isCameraSearchEnabled || !searchQuery.trim()) {
      setDebouncedValue("");
      setShowResults(false);
      return;
    }
    timerRef.current = setTimeout(() => {
      setDebouncedValue(searchQuery.trim());
      setShowResults(true);
    }, 600);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [searchQuery, isCameraSearchEnabled]);

  const searchAssetId =
    debouncedValue ?
      debouncedValue.toUpperCase().startsWith("CAM_") ?
        debouncedValue
      : `CAM_${debouncedValue}`
    : undefined;

  const { data, isFetching } = useCameraSearch({
    assetId: searchAssetId,
    authenticatedFetch,
    directoryBaseUrl: directory(env),
    enabled: authReady && isCameraSearchEnabled && !!debouncedValue,
  });

  const handleFocus = useCallback(() => {
    if (isCameraSearchEnabled && debouncedValue) setShowResults(true);
  }, [debouncedValue, isCameraSearchEnabled]);

  return (
    <View flexDirection="row" alignItems="center" gap="md" style={{ maxWidth: 800 }}>
      <View ref={containerRef} flex={1} style={styles.searchContainer}>
        <View
          style={[styles.inputWrapper, { borderColor: theme.colors.borderCard, backgroundColor: theme.colors.surface }]}
        >
          <View style={styles.iconContainer}>
            <Icon name="search" size={18} color={theme.colors.textMuted} />
          </View>
          <View style={styles.inputArea}>
            <FormInput
              placeholder="Search cameras"
              value={searchQuery}
              onChangeText={onSearchChange}
              onFocus={handleFocus}
              size="medium"
              containerStyle={styles.formInputContainer}
            />
          </View>
          {(isLoading || isFetching) && (
            <View style={styles.spinnerContainer}>
              <ActivityIndicator size="small" color={theme.colors.textMuted} />
            </View>
          )}
        </View>

        {isCameraSearchEnabled &&
          showResults &&
          debouncedValue &&
          !isFetching &&
          data?.result &&
          data.result.cameras.length > 0 &&
          data.result.tenant !== selectedTenant?.id && <CameraSearchResults data={data} isFetching={false} />}
      </View>

      <SortDropdown value={sortBy} onChange={onSortChange} />
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  searchContainer: {
    position: "relative",
    zIndex: 100,
    minWidth: 0,
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "stretch",
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    overflow: "visible",
    height: 44,
  },
  iconContainer: {
    justifyContent: "center",
    paddingLeft: theme.spacing.lg,
  },
  inputArea: {
    flex: 1,
    minWidth: 0,
  },
  formInputContainer: {
    borderWidth: 0,
    backgroundColor: "transparent",
  },
  spinnerContainer: {
    justifyContent: "center",
    paddingHorizontal: theme.spacing.md,
  },
}));
